import React, { useMemo, useState } from "react";
import { Card, Row, Col, Typography, Steps, Space, Image, Grid } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, CreditCardOutlined, FlagOutlined } from "@ant-design/icons";
import CustomCard from "../extraShit/CustomCard";
import { useTranslation } from "react-i18next";
import GamePage from "./GamePage";
import { buildStepItems } from "./stepItems";
import "./gameContent.css";

const { Title, Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;

const decisionMeta = [
    { key: "redBlack", images: [1] },
    { key: "underAbove", images: [1, 2] },
    { key: "betweenOutside", images: [1, 2, 3] },
    { key: "sameSuit", images: [1, 2, 3, 4] },
];

type DecisionCardData = { key: string; images: number[]; title: string; win: string; lose: string };

const DecisionCard: React.FC<{ card: DecisionCardData }> = ({ card }) => (
    <Col xs={24} md={12}>
        <CustomCard title={<Title level={3}>{card.title}</Title>}>
            <Space size={8} align="center" style={{ marginBottom: 8 }}>
                {card.images.map((image) => (
                    <Image key={`${card.key}-${image}`} src={`/cards/Background_${image}.png`} width={46} height={64} alt="" />
                ))}
            </Space>
            <div style={{ marginTop: 10 }}>
                <div className="shitty-game__outcome">
                    <CheckCircleOutlined className="shitty-game__success" />
                    <Text strong>{card.win}</Text>
                </div>
                <div className="shitty-game__outcome">
                    <CloseCircleOutlined className="shitty-game__error" />
                    <Text strong>{card.lose}</Text>
                </div>
            </div>
        </CustomCard>
    </Col>
);

const ShittyGame: React.FC = () => {
    const [current, setCurrent] = useState(0);
    const { t } = useTranslation();
    const screens = useBreakpoint();

    const decisionCards = useMemo(
        () =>
            decisionMeta.map((item) => ({
                ...item,
                title: t(`games.details.shitty.decision.cards.${item.key}.title`),
                win: t(`games.details.shitty.decision.cards.${item.key}.win`),
                lose: t(`games.details.shitty.decision.cards.${item.key}.lose`),
            })),
        [t]
    );

    const pyramidText = t("games.details.shitty.endgame.paragraphs", {
        returnObjects: true,
    }) as string[];

    const steps = useMemo(
        () => [
            {
                title: t("games.details.shitty.steps.decision.title"),
                description: t("games.details.shitty.steps.decision.description"),
                content: (
                    <Card title={<Title level={2}>{t("games.details.shitty.decision.heading")}</Title>}>
                        <Row gutter={[32, 24]}>
                            {decisionCards.slice(0, 2).map((card) => (
                                <DecisionCard key={card.key} card={card} />
                            ))}
                        </Row>
                        <Row gutter={[32, 24]}>
                            {decisionCards.slice(2).map((card) => (
                                <DecisionCard key={card.key} card={card} />
                            ))}
                        </Row>
                        <Row>
                            <Col xs={24}>
                                <CustomCard title={<Title level={3}>{t("games.details.shitty.decision.progressive.title")}</Title>}>
                                    <Paragraph>{t("games.details.shitty.decision.progressive.description1")}</Paragraph>
                                    <Paragraph>{t("games.details.shitty.decision.progressive.description2")}</Paragraph>
                                    <Paragraph>
                                        <CheckCircleOutlined className="shitty-game__success" />
                                        {t("games.details.shitty.decision.progressive.win")}
                                    </Paragraph>
                                    <Paragraph>
                                        <CloseCircleOutlined className="shitty-game__error" />
                                        {t("games.details.shitty.decision.progressive.lose")}
                                    </Paragraph>
                                    <Paragraph type="secondary" style={{ textAlign: "center", marginTop: 12, fontSize: 16 }}>
                                        {t("games.details.shitty.decision.progressive.hint")}
                                    </Paragraph>
                                </CustomCard>
                            </Col>
                        </Row>
                    </Card>
                ),
            },
            {
                title: t("games.details.shitty.steps.endgame.title"),
                description: t("games.details.shitty.steps.endgame.description"),
                content: (
                    <Card title={<Title level={2}>{t("games.details.shitty.endgame.heading")}</Title>}>
                        <CustomCard>
                            <Paragraph>
                                {t("games.details.shitty.endgame.layoutLine1")}
                                <br />
                                {t("games.details.shitty.endgame.layoutLine2")}
                            </Paragraph>
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "auto repeat(5, 38px)",
                                    gridTemplateRows: "50px 74px",
                                    columnGap: 8,
                                    rowGap: 4,
                                    justifyContent: "left",
                                    alignItems: "center",
                                    marginBottom: 16,
                                    minHeight: 120,
                                }}
                            >
                                {[
                                    { key: "top", row: 1, icon: <CheckCircleOutlined className="shitty-game__diagram-success" /> },
                                    { key: "bot", row: 2, icon: <CloseCircleOutlined className="shitty-game__diagram-error" /> },
                                ].map(({ key, row, icon }) => (
                                    <React.Fragment key={key}>
                                        {icon}
                                        {[1, 2, 3, 4].map((n) => (
                                            <Image
                                                key={`${key}-${n}`}
                                                src={`/cards/Background_${n}.png`}
                                                width={38}
                                                height={54}
                                                preview={false}
                                                className="shitty-game__diagram-card"
                                                style={{
                                                    objectFit: "contain",
                                                    gridRow: row,
                                                    gridColumn: n + 2,
                                                }}
                                                alt={`Card ${n}`}
                                            />
                                        ))}
                                    </React.Fragment>
                                ))}
                                <div style={{ gridRow: "1 / 3", gridColumn: 6, display: "flex", justifyContent: "center", alignItems: "center" }}>
                                    <Image
                                        src="/cards/Background.png"
                                        width={38}
                                        height={54}
                                        preview={false}
                                        className="shitty-game__diagram-card"
                                        style={{ objectFit: "contain" }}
                                        alt={t("games.details.shitty.endgame.backgroundAlt")}
                                    />
                                </div>
                            </div>
                            <Space direction="vertical" size={0} style={{ width: "100%" }}>
                                {pyramidText.map((paragraph) => (
                                    <Paragraph style={{ marginBottom: 0 }} key={paragraph}>
                                        {paragraph}
                                    </Paragraph>
                                ))}
                            </Space>
                        </CustomCard>
                    </Card>
                ),
            },
        ],
        [decisionCards, pyramidText, t]
    );

    return (
        <GamePage
            game="shitty"
            tagColor="gold"
            instructionIcons={[<CreditCardOutlined key="decision" />, <FlagOutlined key="endgame" />]}
        >
            <div className="shitty-game">
                <Steps
                    direction={screens?.lg ? "horizontal" : "vertical"}
                    current={current}
                    onChange={setCurrent}
                    items={buildStepItems(steps, current)}
                />
                <div className="shitty-game__step-content">{steps[current]?.content}</div>
            </div>
        </GamePage>
    );
};

export default ShittyGame;
